// Custom Hook for User Management
import { useState, useCallback, useEffect } from 'react';
import {
  fetchUsers as apiFetchUsers,
  fetchUserCount as apiFetchUserCount,
  updateUserStatus as apiUpdateUserStatus,
  deleteUser as apiDeleteUser,
  bulkUserAction as apiBulkUserAction,
  fetchRoles as apiFetchRoles,
  fetchUserStatistics as apiFetchUserStatistics
} from '@/services/superAdminAPI';

/**
 * Custom hook for managing users in the admin dashboard
 */
export const useUserManagement = () => {
  // State Management
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  // Loading States
  const [isLoading, setIsLoading] = useState(false);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [isStatisticsLoading, setIsStatisticsLoading] = useState(false);
  
  // Error State
  const [error, setError] = useState(null);
  
  // Filter State
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    currentPage: 1,
    pageSize: 10
  });

  /**
   * Helper function to handle API errors
   */
  const handleError = useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'An unexpected error occurred';
    
    setError({
      message: errorMessage,
      context,
      status: error.status,
      timestamp: new Date().toISOString()
    });

    // Auto-clear error after 5 seconds
    setTimeout(() => setError(null), 5000);
  }, []);

  /**
   * Load users with current filters
   */
  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await apiFetchUsers({
        skip: (filters.currentPage - 1) * filters.pageSize,
        limit: filters.pageSize,
        search: filters.search,
        role: filters.role,
        status: filters.status,
      });

      // Ensure we always work with an array, even if API returns null/undefined
      const safeUsers = Array.isArray(data) ? data : [];

      // Transform API response to match component expectations
      const transformedUsers = safeUsers.map(user => ({
        user_id: user.id,
        username: user.name,
        email: user.email,
        full_name: user.name,
        role: user.role_name,
        role_description: user.role_description,
        is_active: user.status === 'ACTIVE',
        status: user.status,
        last_login: user.last_login_at,
        created_at: user.created_at,
        updated_at: user.updated_at,
        permissions: user.permissions
      }));

      setUsers(transformedUsers);
      return transformedUsers;
    } catch (err) {
      handleError(err, 'loadUsers');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [filters, handleError]);

  /**
   * Load total user count
   */
  const loadUserCount = useCallback(async () => {
    try {
      const count = await apiFetchUserCount({
        search: filters.search,
        role: filters.role,
        status: filters.status
      });
      setTotalUsers(count);
      return count;
    } catch (err) {
      handleError(err, 'loadUserCount');
      throw err;
    }
  }, [filters.search, filters.role, filters.status, handleError]);

  /**
   * Load available roles
   */
  const loadRoles = useCallback(async () => {
    try {
      setIsRolesLoading(true);
      const roles = await apiFetchRoles();
      
      // Ensure we always work with an array, even if API returns null/undefined
      const safeRoles = Array.isArray(roles) ? roles : [];
      
      // Transform API response
      const transformedRoles = safeRoles.map(role => ({
        value: role.name,
        label: role.description,
        name: role.name,
        description: role.description,
        user_count: role.user_count,
        permissions: role.permissions
      }));
      
      setAvailableRoles(transformedRoles);
      return transformedRoles;
    } catch (err) {
      handleError(err, 'loadRoles');
      throw err;
    } finally {
      setIsRolesLoading(false);
    }
  }, [handleError]);

  /**
   * Load user statistics
   */
  const loadStatistics = useCallback(async () => {
    try {
      setIsStatisticsLoading(true);
      const stats = await apiFetchUserStatistics();
      setStatistics(stats);
      return stats;
    } catch (err) {
      handleError(err, 'loadStatistics');
      throw err;
    } finally {
      setIsStatisticsLoading(false);
    }
  }, [handleError]);

  /**
   * Toggle user status (activate/deactivate)
   */
  const updateUserStatus = useCallback(async (userId, isActive) => {
    try {
      setError(null);
      const newStatus = isActive ? 'ACTIVE' : 'DISABLED';
      const result = await apiUpdateUserStatus(userId, newStatus);
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.user_id === userId 
            ? { ...user, is_active: isActive, status: newStatus }
            : user
        )
      );

      return result;
    } catch (err) {
      handleError(err, 'updateUserStatus');
      throw err;
    }
  }, [handleError]);

  /**
   * Delete a single user
   */
  const deleteUserById = useCallback(async (userId, reason = null) => {
    try {
      setError(null);
      const result = await apiDeleteUser(userId, reason);
      
      // Remove from local state
      setUsers(prevUsers => prevUsers.filter(user => user.user_id !== userId));
      setTotalUsers(prev => Math.max(0, prev - 1));

      return result;
    } catch (err) {
      handleError(err, 'deleteUserById');
      throw err;
    }
  }, [handleError]);

  /**
   * Perform bulk action on multiple users
   */
  const performBulkAction = useCallback(async (userIds, action, reason = null) => {
    try {
      setError(null);
      
      // Map frontend actions to API actions
      const actionMap = {
        'ACTIVATE': 'enable',
        'DEACTIVATE': 'disable',
        'DELETE': 'delete'
      };
      
      const apiAction = actionMap[action] || action.toLowerCase();
      const result = await apiBulkUserAction(userIds, apiAction, reason);

      // Refresh user list after bulk action
      await loadUsers();
      await loadUserCount();

      return result;
    } catch (err) {
      handleError(err, 'performBulkAction');
      throw err;
    }
  }, [handleError, loadUsers, loadUserCount]);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      // Reset to page 1 when filters change (except page change)
      currentPage: newFilters.currentPage !== undefined ? newFilters.currentPage : 1
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      role: '',
      status: '',
      currentPage: 1,
      pageSize: 10
    });
  }, []);

  /**
   * Refresh all data
   */
  const refreshData = useCallback(async () => {
    try {
      await Promise.all([
        loadUsers(),
        loadUserCount(),
        loadStatistics()
      ]);
    } catch (err) {
      // Errors are already handled in individual functions
      console.error('Error refreshing data:', err);
    }
  }, [loadUsers, loadUserCount, loadStatistics]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load roles on mount
  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  // Load users and count when filters change
  useEffect(() => {
    loadUsers();
    loadUserCount();
  }, [filters, loadUsers, loadUserCount]);

  return {
    // State
    users,
    totalUsers,
    availableRoles,
    statistics,
    filters,
    error,
    
    // Loading States
    isLoading,
    isRolesLoading,
    isStatisticsLoading,
    
    // Data Loading Functions
    loadUsers,
    loadUserCount,
    loadRoles,
    loadStatistics,
    refreshData,
    
    // User Management Functions
    updateUserStatus,
    deleteUserById,
    performBulkAction,
    
    // Filter Management
    updateFilters,
    clearFilters,
    
    // Error Management
    clearError
  };
};

export default useUserManagement;
